/**
 * CompactParticipantItem - Affichage compact d'un participant
 * Utilisé dans la liste des participants et le mode ajout
 */
import React, { memo } from 'react';

const CompactParticipantItem = memo(({
  user,
  isSelected = false,
  showCheckbox = false,
  isOnSite = false,
  userAvailability = {},
  conflicts = [],
  onToggle,
  onToggleOnSite,
  disabled = false
}) => {
  const availability = userAvailability[user.id];
  const status = availability?.status || 'available';
  const hasConflict = conflicts.some(c => c.user_id === user.id);
  const isHRUnavailable = status === 'hr_unavailable';
  const isBlocked = isHRUnavailable && !isSelected;

  // Rendu des infos de mission/conflit
  const renderMissionInfo = () => {
    // Indisponibilité RH (CP, RTT, congés, etc.)
    if (isHRUnavailable) {
      return (
        <span style={{
          fontSize: '11px',
          color: '#721c24',
          background: '#f8d7da',
          padding: '2px 6px',
          borderRadius: '4px',
          marginLeft: '8px',
          fontWeight: '600'
        }}>
          {availability?.reason || 'Congé'}
        </span>
      );
    }

    if (status === 'busy') {
      const groupInfo = availability?.group_code || availability?.group_name || availability?.reason;
      return (
        <span style={{
          fontSize: '11px',
          color: '#856404',
          background: '#fff3cd',
          padding: '2px 6px',
          borderRadius: '4px',
          marginLeft: '8px',
          fontWeight: '500'
        }}>
          {groupInfo || 'Déjà affecté'}
        </span>
      );
    }

    if (status === 'holiday') {
      return (
        <span style={{
          fontSize: '11px',
          color: '#0c5460',
          background: '#d1ecf1',
          padding: '2px 6px',
          borderRadius: '4px',
          marginLeft: '8px',
          fontWeight: '500'
        }}>
          {availability?.reason || 'Jour férié'}
        </span>
      );
    }

    if (hasConflict) {
      const userConflict = conflicts.find(c => c.user_id === user.id);
      if (userConflict?.conflicts?.[0]) {
        const conflictInfo = userConflict.conflicts[0];
        return (
          <span style={{
            fontSize: '11px',
            color: '#856404',
            background: '#fff3cd',
            padding: '2px 6px',
            borderRadius: '4px',
            marginLeft: '8px',
            fontWeight: '500'
          }}>
            {conflictInfo.room_code || conflictInfo.room_name || conflictInfo.prestation_number || 'Conflit'}
            {userConflict.conflicts.length > 1 && ` +${userConflict.conflicts.length - 1}`}
          </span>
        );
      }
    }

    return null;
  };

  return (
    <div
      className={`pi-participant-item ${isSelected ? 'pi-participant-item--selected' : ''} ${hasConflict ? 'pi-participant-item--conflict' : ''} ${isBlocked ? 'pi-participant-item--blocked' : ''}`}
      style={isBlocked ? { opacity: 0.6, background: '#f8d7da', borderLeft: '4px solid #dc3545' } : {}}
    >
      <div className="pi-participant-item__main">
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle?.(user.id)}
            className="pi-participant-item__checkbox"
            disabled={isBlocked || disabled}
            title={isBlocked ? `Indisponible: ${availability?.reason || 'Congé'}` : ''}
            style={isBlocked ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
          />
        )}

        <div className="pi-participant-item__info">
          <div className="pi-participant-item__name">
            <span>{user.full_name}</span>
            {renderMissionInfo()}
            {hasConflict && (
              <span className="pi-status-badge pi-status-badge--conflict">
                CONFLIT
              </span>
            )}
          </div>

          <div className="pi-participant-item__meta">
            {user.group_name || 'Sans groupe'} • {user.role_name}
          </div>
        </div>

        {/* Actions pour participants sélectionnés (mode vue) */}
        {!showCheckbox && isSelected && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Case "Sur place" */}
            {onToggleOnSite && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  color: isOnSite ? '#155724' : '#6c757d',
                  background: isOnSite ? '#d4edda' : '#f8f9fa',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: isOnSite ? '1px solid #c3e6cb' : '1px solid #e9ecef'
                }}
                title="Cocher si le participant est déjà sur place (pas de trajet)"
              >
                <input
                  type="checkbox"
                  checked={isOnSite}
                  onChange={(e) => onToggleOnSite(user.id, e.target.checked)}
                  style={{ margin: 0 }}
                />
                <span>Sur place</span>
              </label>
            )}

            {/* Bouton retirer */}
            <button
              type="button"
              onClick={() => onToggle?.(user.id)}
              className="pi-quick-btn pi-quick-btn--danger"
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              ✗
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

CompactParticipantItem.displayName = 'CompactParticipantItem';

export default CompactParticipantItem;
